import { useState, useEffect, useCallback } from 'react';
import { safeGetItem, safeSetItem } from '@/lib/safeStorage';

const STORAGE_KEY = 'human-rooms-bookmarks';

interface BookmarksState {
  roomIds: string[];
  lastUpdated: number;
}

const getDefaultState = (): BookmarksState => ({
  roomIds: [],
  lastUpdated: Date.now(),
});

export const useBookmarks = () => {
  const [state, setState] = useState<BookmarksState>(getDefaultState);

  // Load from localStorage
  useEffect(() => {
    const stored = safeGetItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setState({ ...getDefaultState(), ...parsed });
      } catch {
        setState(getDefaultState());
      }
    }
  }, []);

  // Save to localStorage
  const saveState = useCallback((newState: BookmarksState) => {
    safeSetItem(STORAGE_KEY, JSON.stringify(newState));
    setState(newState);
  }, []);

  const toggleBookmark = useCallback((roomId: string) => {
    setState(prev => {
      const isBookmarked = prev.roomIds.includes(roomId);
      const newRoomIds = isBookmarked
        ? prev.roomIds.filter(id => id !== roomId)
        : [...prev.roomIds, roomId];
      
      const newState = {
        roomIds: newRoomIds,
        lastUpdated: Date.now(),
      };
      
      safeSetItem(STORAGE_KEY, JSON.stringify(newState));
      return newState;
    });
  }, []);

  const isBookmarked = useCallback((roomId: string): boolean => {
    return state.roomIds.includes(roomId);
  }, [state.roomIds]);

  const clearBookmarks = useCallback(() => {
    const newState = getDefaultState();
    saveState(newState);
  }, [saveState]);

  return {
    bookmarks: state.roomIds,
    bookmarkCount: state.roomIds.length,
    toggleBookmark,
    isBookmarked,
    clearBookmarks,
  };
};
